import { useEffect } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import { Alert, Card, CardContent, Divider, Grid, Typography } from "@mui/joy";
import { Checkbox, TextField } from "../../../../shared";
import { Info } from "lucide-react";

export function PropertyDetails() {
    const { setValue, getValues } = useFormContext();
    const noEagleViewId = useWatch({ name: "noEagleViewId" }) ?? false;
    const copyAddressFromCustomer = useWatch({ name: "copyAddressFromCustomer" }) ?? false;

    useEffect(() => {
        if (copyAddressFromCustomer) {
            const customerAddress = {
                customerStreetAddress: getValues("customerStreetAddress"),
                customerCity: getValues("customerCity"),
                customerState: getValues("customerState"),
                customerZipCode: getValues("customerZipCode"),
                customerCountry: getValues("customerCountry"),
            };

            setValue("propertyStreetAddress", customerAddress.customerStreetAddress || "");
            setValue("propertyCity", customerAddress.customerCity || "");
            setValue("propertyState", customerAddress.customerState || "");
            setValue("propertyZipCode", customerAddress.customerZipCode || "");
            setValue("propertyCountry", customerAddress.customerCountry || "Denver County");
        }
    }, [copyAddressFromCustomer, setValue, getValues]);

    return (
        <Grid container spacing={2} mt={0}>
            {!noEagleViewId && (
                <Grid xs={12}>
                    <TextField
                        label={"EagleView Order ID"}
                        helperText={"Enter the EagleView order ID or project ID associated with this property"}
                        name={"evorderid"}
                    />
                </Grid>
            )}

            <Grid xs={12} mt={1}>
                <Checkbox
                    name="noEagleViewId"
                    label="I do not have an EagleView Order ID"
                    size="sm"
                    defaultChecked={false}
                    helperText={
                        !noEagleViewId
                            ? "Check this box if you don't have an EagleView Order ID"
                            : "You can still enter the property address below"
                    }
                />
            </Grid>

            {noEagleViewId && (
                <Grid xs={12} mt={1}>
                    <Measurements />
                </Grid>
            )}

            <Grid xs={12} mt={1}>
                <Checkbox
                    name="copyAddressFromCustomer"
                    label="Copy address from customer details"
                    size="sm"
                    defaultChecked={false}
                    helperText="Check this box to automatically copy the customer address to the property address fields"
                />
            </Grid>

            <Grid xs={12}>
                <TextField
                    label={"Property Street Address"}
                    helperText={"Street name and house/building number of the property"}
                    placeholder={"123 Main St"}
                    name={"propertyStreetAddress"}
                />
            </Grid>
            <Grid xs={6}>
                <TextField
                    label={"Property City"}
                    helperText={"City where the property is located"}
                    placeholder={"Denver"}
                    name={"propertyCity"}
                />
            </Grid>
            <Grid xs={6}>
                <TextField
                    label={"Property State"}
                    helperText={"State where the property is located"}
                    placeholder={"CO"}
                    name={"propertyState"}
                />
            </Grid>
            <Grid xs={6}>
                <TextField
                    label={"Property ZIP Code"}
                    helperText={"5-digit ZIP code of the property"}
                    placeholder={"80202"}
                    name={"propertyZipCode"}
                />
            </Grid>
            <Grid xs={6}>
                <TextField
                    label={"Property County"}
                    helperText={"County where the property is located"}
                    placeholder={"Denver County"}
                    name={"propertyCountry"}
                />
            </Grid>
        </Grid>
    );
}

function Measurements() {
    return (
        <Card sx={{ pt: 1.5 }}>
            <CardContent>
                <Alert
                    startDecorator={<Info size={16} />}
                    color="warning"
                    variant="outlined"
                    sx={{ background: "var(--standard-palette-warning-softBg)", mb: 1 }}
                >
                    Since you don't have an EagleView Order ID, you will need to manually enter the roof measurements
                    details for this project.
                </Alert>
                <Typography level="title-md">Roof Measurements</Typography>
                <Typography level="body-sm" mb={1} color="neutral">
                    Enter the roof measurements in square feet (sq ft) for area measurements and linear feet (ft) for
                    length measurements.
                </Typography>

                <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                        <TextField
                            label="Area"
                            name="measurementArea"
                            helperText="Total roof area in square feet (sq ft)"
                            placeholder="0.00"
                            type="number"
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <TextField
                            label="Ridge"
                            name="measurementRidge"
                            helperText="Ridge length in linear feet (ft)"
                            placeholder="0.00"
                            type="number"
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <TextField
                            label="Hip"
                            name="measurementHip"
                            helperText="Hip length in linear feet (ft)"
                            placeholder="0.00"
                            type="number"
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <TextField
                            label="Valley"
                            name="measurementValley"
                            helperText="Valley length in linear feet (ft)"
                            placeholder="0.00"
                            type="number"
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <TextField
                            label="Eave"
                            name="measurementEave"
                            helperText="Eave length in linear feet (ft)"
                            placeholder="0.00"
                            type="number"
                        />
                    </Grid>
                    <Grid xs={12} md={6}>
                        <TextField
                            label="Rake"
                            name="measurementRake"
                            helperText="Rake length in linear feet (ft)"
                            placeholder="0.00"
                            type="number"
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
